import React, { useState } from 'react';
import {
  Box, Grid, Typography, FormControl, InputLabel, Select, MenuItem,
  Slider, Stack, Chip, Button, Collapse, TextField
} from '@mui/material';
import { FilterList, ExpandMore, ExpandLess, SearchOff } from '@mui/icons-material';
import { brand, border } from '../../theme/colors';
import VerifierCard from './VerifierCard';

const VerifierMarketplaceTab = ({
  verifiers,
  aiRecommendations,
  onHireClick,
  filters,
  onFilterChange,
  loading
}) => {
  const [showFilters, setShowFilters] = useState(false);
  const [localFilters, setLocalFilters] = useState(filters);

  const handleFilterChange = (field, value) => {
    const newFilters = { ...localFilters, [field]: value };
    setLocalFilters(newFilters);
    if (onFilterChange) {
      onFilterChange(newFilters);
    }
  };

  const clearFilters = () => {
    const defaultFilters = {
      tier: '',
      city: '',
      specializations: [],
      minRating: 0,
      maxPrice: null,
      sortBy: 'average_rating'
    };
    setLocalFilters(defaultFilters);
    if (onFilterChange) {
      onFilterChange(defaultFilters);
    }
  };

  const isAIRecommended = (verifierId) => {
    return aiRecommendations.some(rec => rec.user_id === verifierId);
  };

  const getAIRecommendation = (verifierId) => {
    return aiRecommendations.find(rec => rec.user_id === verifierId);
  };

  return (
    <Box>
      {/* Filter Bar */}
      <Box sx={{ mb: 3 }}>
        <Button
          startIcon={<FilterList />}
          endIcon={showFilters ? <ExpandLess /> : <ExpandMore />}
          onClick={() => setShowFilters(!showFilters)}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            color: brand.primary,
            mb: 2
          }}
        >
          {showFilters ? 'Hide Filters' : 'Show Filters'}
        </Button>

        <Collapse in={showFilters}>
          <Box
            sx={{
              backgroundColor: '#f9f9f9',
              borderRadius: 2,
              p: 3,
              border: `1px solid ${border.default}`
            }}
          >
            <Grid container spacing={3}>
              {/* Tier Filter */}
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Tier</InputLabel>
                  <Select
                    value={localFilters.tier}
                    label="Tier"
                    onChange={(e) => handleFilterChange('tier', e.target.value)}
                  >
                    <MenuItem value="">All Tiers</MenuItem>
                    <MenuItem value="general">General</MenuItem>
                    <MenuItem value="professional">Professional</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* City Filter */}
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="City"
                  placeholder="e.g. Harare, Bulawayo"
                  value={localFilters.city}
                  onChange={(e) => handleFilterChange('city', e.target.value)}
                />
              </Grid>

              {/* Sort By */}
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Sort By</InputLabel>
                  <Select
                    value={localFilters.sortBy}
                    label="Sort By"
                    onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                  >
                    <MenuItem value="average_rating">Highest Rating</MenuItem>
                    <MenuItem value="total_verifications">Most Verifications</MenuItem>
                    <MenuItem value="years_experience">Most Experience</MenuItem>
                    <MenuItem value="created_at">Newest First</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Minimum Rating */}
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" gutterBottom>
                  Minimum Rating: {localFilters.minRating}+
                </Typography>
                <Slider
                  value={localFilters.minRating}
                  onChange={(e, value) => handleFilterChange('minRating', value)}
                  min={0}
                  max={5}
                  step={0.5}
                  marks
                  valueLabelDisplay="auto"
                  sx={{
                    color: brand.primary,
                    '& .MuiSlider-thumb': {
                      backgroundColor: brand.primary
                    },
                    '& .MuiSlider-track': {
                      backgroundColor: brand.primary
                    },
                    '& .MuiSlider-rail': {
                      backgroundColor: border.default
                    }
                  }}
                />
              </Grid>

              {/* Clear Filters */}
              <Grid item xs={12} sm={6} md={3} display="flex" alignItems="flex-end">
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={clearFilters}
                  sx={{
                    textTransform: 'none',
                    borderColor: brand.primary,
                    color: brand.primary,
                    '&:hover': {
                      borderColor: brand.primaryDark,
                      backgroundColor: '#f3f0ff'
                    }
                  }}
                >
                  Clear Filters
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Collapse>
      </Box>

      {/* Results Count */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {verifiers.length} {verifiers.length === 1 ? 'verifier' : 'verifiers'} found
        {aiRecommendations.length > 0 && (
          <Chip
            label={`${aiRecommendations.length} AI recommended`}
            size="small"
            sx={{
              ml: 2,
              backgroundColor: brand.primary,
              color: 'white',
              fontWeight: 600
            }}
          />
        )}
      </Typography>

      {/* Verifier Grid */}
      {verifiers.length === 0 ? (
        (() => {
          const hasActiveFilters = filters.tier || filters.city ||
            (filters.specializations && filters.specializations.length > 0) || filters.minRating > 0;
          return (
            <Box sx={{ textAlign: 'center', py: 8, px: 3 }}>
              <SearchOff sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                {hasActiveFilters ? 'No verifiers match your filters' : 'No verified verifiers found'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {hasActiveFilters
                  ? 'Try widening your search — remove the city filter, lower the minimum rating, or select a different tier.'
                  : 'Verified verifiers will appear here once they are available in your area.'}
              </Typography>
              {hasActiveFilters && (
                <Button
                  variant="outlined"
                  onClick={clearFilters}
                  sx={{ textTransform: 'none', borderColor: brand.primary, color: brand.primary }}
                >
                  Clear All Filters
                </Button>
              )}
            </Box>
          );
        })()
      ) : (
        <Grid container spacing={3}>
          {verifiers.map((verifier) => {
            const aiRec = getAIRecommendation(verifier.user_id);
            return (
              <Grid item xs={12} sm={6} md={4} key={verifier.user_id}>
                <VerifierCard
                  verifier={verifier}
                  onHireClick={onHireClick}
                  isAIRecommended={!!aiRec}
                  aiMatchScore={aiRec?.match_score}
                  aiReasons={aiRec?.reasons || []}
                />
              </Grid>
            );
          })}
        </Grid>
      )}
    </Box>
  );
};

export default VerifierMarketplaceTab;
